/**
 * A conversation with real answers. An agent does not narrate: what it said at once stands first, the
 * work it did out of sight sits behind one small line ("Worked for…"), and the result follows — text,
 * a file it made (a card of its own), a card that waits for a decision. A line it wrote to another
 * agent appears where it happened and opens what the two of them said.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { ArrowLeftRight, Brain, Calculator, ChartPie, ChevronRight, Clock, CornerDownRight, Download, FileText, Landmark, Search, Wrench } from 'lucide-react'
import type { ChatMessage, Citation, ToolEvent } from '@shared/types'
import { Markdown } from '../components/chat/Markdown'
import { GroupCard, PlanCard, TicketCard, WatchCard } from './Cards'
import { chatPage } from './api'
import { Composer } from './Agents'
import type { Agent, Group } from './data'
import { WorkspaceMark } from './icons'
import { Btn, Glyph, I, cx, type Icon, type Palette } from './kit'
import { useDemo, type Live } from './store'

const clock = (ms: number) => new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
const dayOf = (ms: number) => new Date(ms).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
const span = (ms: number) => {
  const s = Math.max(1, Math.round(ms / 1000))
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`
}
const size = (n?: number) => (n == null ? '' : n < 1024 ? `${n} B` : n < 1048576 ? `${Math.round(n / 1024)} KB` : `${(n / 1048576).toFixed(1)} MB`)

/** A small line under an answer: something it did besides answering. */
const note = 'flex h-6 items-center gap-[6px] rounded-[6px] text-[12px] tracking-normal text-ws-fg-3 transition-colors duration-150'
const noteBtn = cx(note, '-mx-[6px] px-[6px] hover:bg-ws-hover hover:text-ws-fg-2')

/** How long a job has been running. */
function Elapsed({ since }: { since: number }) {
  const [, tick] = useState(0)
  useEffect(() => {
    const t = window.setInterval(() => tick((n) => n + 1), 1000)
    return () => window.clearInterval(t)
  }, [])
  const s = Math.max(0, Math.floor((Date.now() - since) / 1000))
  return (
    <span className="tabular-nums">
      {Math.floor(s / 60)}:{String(s % 60).padStart(2, '0')}
    </span>
  )
}

/** The ids of the cards an answer produced (a worker plan, an order, a watcher…). */
function drafted(events: ToolEvent[] | undefined, tool: string, key: string): string[] {
  const ids: string[] = []
  for (const e of events ?? []) {
    const id = e.name === tool && e.status === 'done' ? (e.output as Record<string, unknown> | undefined)?.[key] : null
    if (typeof id === 'string' && !ids.includes(id)) ids.push(id)
  }
  return ids
}
interface Made {
  name: string
  title?: string
  size?: number
  url?: string
}
/** The files an answer handed over. */
const filesOf = (events: ToolEvent[] | undefined): Made[] => (events ?? []).flatMap((e) => (e.name === 'present_files' && e.status === 'done' ? (((e.output as { files?: Made[] } | undefined)?.files ?? []).filter((f) => f.url) as Made[]) : []))
/** What an answer did to the agent's routines and memory. */
const VERB: Record<string, string> = { schedule_create: 'Created', schedule_update: 'Updated', schedule_delete: 'Deleted' }
const routineEvents = (events: ToolEvent[] | undefined) =>
  (events ?? []).flatMap((e) => {
    if (!VERB[e.name] || e.status !== 'done') return []
    const out = e.output as { task?: { id?: string; title?: string } } | undefined
    const input = e.input as { title?: string; id?: string } | undefined
    return [{ verb: VERB[e.name], id: out?.task?.id ?? input?.id, name: out?.task?.title ?? input?.title ?? 'routine' }]
  })
const saved = (events: ToolEvent[] | undefined) => (events ?? []).some((e) => e.name === 'remember' && e.status === 'done')
/** The other agents this answer wrote to: where it happened, and the talk it opens. */
const wires = (events: ToolEvent[] | undefined) =>
  (events ?? []).flatMap((e) => {
    if (e.name !== 'message_agent') return []
    const out = e.output as { agentId?: string; agentName?: string; chatId?: string } | undefined
    const input = e.input as { agent?: string } | undefined
    return [{ id: e.id, name: out?.agentName ?? input?.agent ?? 'another agent', agentId: out?.agentId, chatId: out?.chatId, running: e.status === 'running' }]
  })
/** Was anything done out of sight (beyond writing to another agent or noting the plan)? */
const worked = (events: ToolEvent[] | undefined) => (events ?? []).some((e) => e.name !== 'update_plan')

/* ── what an answer did out of sight, step by step ───────────────────── */

const STEP: [RegExp, Icon][] = [
  [/^(remember|forget|recall|search_project|memory)/, Brain],
  [/(search|fetch|browse|news|read_url)/, Search],
  [/(agent|delegate|spawn)/, CornerDownRight],
  [/(calc|code|python|run_|sandbox|execute)/, Calculator],
  [/(file|draft|report|chart|present)/, FileText],
  [/(schedule|watch|scan|wake|task)/, Clock],
  [/(order|broker|worker|robinhood|ibkr|position)/, Landmark],
  [/(portfolio|quote|market|eodhd|price|technical|fundamental)/, ChartPie],
]
const stepIcon = (name: string): Icon => STEP.find(([re]) => re.test(name))?.[1] ?? Wrench
const words = (name: string) => name.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())

/** The steps behind "Worked for…": each thing it thought through or did, in the order it happened. */
function Steps({ msg }: { msg: ChatMessage }) {
  const [open, setOpen] = useState<number | null>(null)
  const events = msg.toolEvents ?? []
  const row = 'flex min-h-6 items-center gap-2 text-[12px] tracking-normal text-ws-fg-3'
  // the order it really happened in, when the answer kept it; else the tools as they were called
  type Step = { kind: 'thought'; text: string } | { kind: 'tool'; e: ToolEvent }
  const steps: Step[] = msg.segments?.length
    ? msg.segments.flatMap((x): Step[] => (x.kind === 'reasoning' ? (x.text.trim() ? [{ kind: 'thought', text: x.text.trim() }] : []) : events.filter((e) => e.id === x.eventId).map((e) => ({ kind: 'tool', e }))))
    : events.map((e) => ({ kind: 'tool', e }))
  return (
    <ul className="m-0 mt-1 list-none space-y-px border-l border-ws-edge p-0 pl-3">
      {steps.map((st, i) =>
        st.kind === 'thought' ? (
          <li key={i}>
            <button type="button" aria-expanded={open === i} onClick={() => setOpen(open === i ? null : i)} className={cx(row, 'text-left transition-colors hover:text-ws-fg-2')}>
              <I icon={Brain} size={13} className="shrink-0 text-ws-fg-4" />
              <span className="min-w-0">Thought it through</span>
              <ChevronRight size={11} strokeWidth={2.2} className={cx('shrink-0 text-ws-fg-4 transition-transform duration-150', open === i && 'rotate-90')} />
            </button>
            {open === i && <p className="m-0 mb-1 ml-[21px] max-w-[640px] whitespace-pre-wrap text-[12px] leading-[18px] tracking-normal text-ws-fg-4">{st.text}</p>}
          </li>
        ) : st.e.name === 'update_plan' ? null : (
          <li key={i} className={row}>
            <I icon={stepIcon(st.e.name)} size={13} className={cx('shrink-0', st.e.status === 'error' ? 'text-ws-down' : 'text-ws-fg-4')} />
            <span className="min-w-0">
              {st.e.label || words(st.e.name)}
              {st.e.agentRole ? <span className="text-ws-fg-4"> · {st.e.agentRole}</span> : null}
              {st.e.status === 'error' && <span className="text-ws-fg-4"> · did not work</span>}
            </span>
          </li>
        ),
      )}
    </ul>
  )
}

/** Where an answer read what it says: the pages, by name. Closed until asked for. */
function Sources({ list }: { list: Citation[] }) {
  const site = (url: string) => {
    try {
      return new URL(url).hostname.replace(/^www\./, '')
    } catch {
      return url
    }
  }
  // (a search result often carries its number as its title: the site says more)
  const name = (c: Citation) => ((c.title ?? '').trim() && !/^\d+$/.test((c.title ?? '').trim()) ? (c.title ?? '').trim() : site(c.url))
  return (
    <ol className="m-0 mt-1 list-none space-y-px border-l border-ws-edge p-0 pl-3">
      {list.map((c, i) => (
        <li key={`${c.url}${i}`} className="flex min-h-6 items-center gap-2 text-[12px] tracking-normal">
          <span className="w-4 shrink-0 text-right tabular-nums text-ws-fg-4">{i + 1}</span>
          <a href={c.url} target="_blank" rel="noreferrer noopener" title={c.url} className="min-w-0 truncate text-ws-fg-3 transition-colors hover:text-ws-fg">
            {name(c)}
            {name(c) !== site(c.url) && <span className="text-ws-fg-4"> · {site(c.url)}</span>}
          </a>
        </li>
      ))}
    </ol>
  )
}

const KIND = (name: string) => {
  const ext = name.split('.').pop()?.toLowerCase() ?? ''
  return ({ pdf: 'PDF', xlsx: 'XLS', xls: 'XLS', csv: 'CSV', png: 'IMG', jpg: 'IMG', jpeg: 'IMG', svg: 'IMG', docx: 'DOC', pptx: 'PPT', md: 'MD', txt: 'TXT', json: 'JSON' } as Record<string, string>)[ext] ?? ext.slice(0, 4).toUpperCase() ?? 'FILE'
}
/** A file as a message of its own: its kind, its name, its size — press it to open, or take it with you. */
function FileCard({ file }: { file: Made }) {
  return (
    <div className="flex h-[52px] w-full max-w-[360px] items-center rounded-[10px] border border-ws-edge bg-ws-panel pr-1 transition-colors duration-150 hover:border-[#303030] hover:bg-[#1a1a1a]">
      <a href={file.url} target="_blank" rel="noreferrer" className="flex h-full min-w-0 flex-1 items-center gap-3 rounded-[10px] pl-[10px] text-left">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] bg-ws-bg text-[10px] font-semibold leading-none tracking-[0.03em] text-ws-fg-2" aria-hidden>
          {KIND(file.name)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium leading-5 text-ws-fg">{file.title || file.name}</span>
          <span className="block truncate text-[12px] leading-4 tracking-normal text-ws-fg-3">{[file.title ? file.name : '', size(file.size)].filter(Boolean).join(' · ')}</span>
        </span>
      </a>
      <a href={file.url} download={file.name} aria-label={`Download ${file.name}`} title="Download" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-ws-icon transition-colors duration-150 hover:bg-ws-raise hover:text-ws-fg">
        <Download size={16} strokeWidth={1.8} />
      </a>
    </div>
  )
}

/** What you attached rides on your message as small chips (the server keeps their names at the end of the text). */
function splitAttachments(content: string): { text: string; files: string[] } {
  const i = content.lastIndexOf('📎')
  if (i < 0) return { text: content, files: [] }
  const head = content.slice(0, content.indexOf('📎')).replace(/\s+$/, '')
  const files = content
    .slice(content.indexOf('📎'))
    .split('📎')
    .map((x) => x.trim())
    .filter(Boolean)
  return { text: head, files }
}

function Face({ agent, pulse }: { agent?: Agent; pulse?: boolean }) {
  return <span className={cx('mt-[2px] shrink-0', pulse && 'animate-ws-pulse')}>{agent ? <Glyph palette={agent.palette} /> : <WorkspaceMark size={16} />}</span>
}

/** Who wrote an answer: the agent named on it, else the one this conversation belongs to. */
const speakerOf = (m: { meta?: ChatMessage['meta'] }, agents: Agent[], owner?: Agent) => (m.meta?.agent ? (agents.find((a) => a.id === m.meta!.agent!.id) ?? ({ id: m.meta.agent.id, name: m.meta.agent.name, palette: 'syntax' as Palette } as Agent)) : owner)

function Answer({ msg, agents, owner, live }: { msg: ChatMessage; agents: Agent[]; owner?: Agent; live?: Live }) {
  const setS = useDemo((s) => s.set)
  const openChat = useDemo((s) => s.openChat)
  const routines = useDemo((s) => s.routines)
  const [log, setLog] = useState(false)
  const [src, setSrc] = useState(false)
  const a = speakerOf(msg, agents, owner)
  const events = msg.toolEvents
  const ack = live ? live.ack : (msg.meta?.ack ?? '')
  const busy = !!live && !msg.content
  const files = filesOf(events)
  const made = routineEvents(events)
  const out = worked(events)
  const took = msg.meta?.durationMs
  const cards: ReactNode[] = [
    ...drafted(events, 'worker_draft', 'workerId').map((id) => <PlanCard key={id} id={id} />),
    ...drafted(events, 'worker_group_draft', 'groupId').map((id) => <GroupCard key={id} id={id} />),
    ...drafted(events, 'order_draft', 'ticketId').map((id) => <TicketCard key={id} id={id} />),
    ...[...drafted(events, 'watch_create', 'watcherId'), ...drafted(events, 'scan_create', 'scanId')].map((id) => <WatchCard key={id} id={id} />),
  ]
  const cited = !live && msg.citations && msg.citations.length > 0 ? msg.citations : null
  const notes = !live && (out || cited || made.length > 0 || saved(events) || msg.meta?.stopped)
  return (
    <div className="flex animate-ws-in gap-3">
      <Face agent={a} pulse={busy} />
      <div className="min-w-0 flex-1">
        <div className="flex h-5 items-center gap-2">
          <span className="font-medium text-ws-fg">{a?.name ?? 'Libera'}</span>
          {!live && <span className="text-[12px] tracking-normal text-ws-fg-4">{clock(msg.createdAt)}</span>}
          {msg.meta?.trigger && <span className="truncate text-[12px] tracking-normal text-ws-fg-4">· {msg.meta.trigger.kind === 'agent' ? `asked by ${msg.meta.trigger.title}` : msg.meta.trigger.title}</span>}
        </div>
        {ack && <p className="m-0 mt-1 whitespace-pre-wrap leading-[22px] tracking-normal text-[#d8d8d8]">{ack}</p>}
        {wires(events).map((w) => (
          <div key={w.id} className="mt-1 flex">
            <button type="button" disabled={!w.chatId} onClick={() => w.chatId && void openChat(w.chatId)} className={noteBtn}>
              {w.running ? 'Messaging' : 'Messaged'}
              <Glyph palette={agents.find((x) => x.id === w.agentId || x.name === w.name)?.palette ?? 'syntax'} size={14} />
              <span className="text-ws-fg-2">{w.name}</span>
            </button>
          </div>
        ))}
        {busy && (
          <div className="mt-1 flex h-5 items-center gap-2 text-[12px] tracking-normal text-ws-fg-3">
            <span className="truncate">{live.toolEvents.length || live.ack ? 'Working in the background' : 'Thinking'}</span>
            <span className="text-ws-fg-4">
              <Elapsed since={live.startedAt} />
            </span>
          </div>
        )}
        {files.length > 0 && (
          <div className="mt-2 flex flex-col gap-2">
            {files.map((f) => (
              <FileCard key={f.url} file={f} />
            ))}
          </div>
        )}
        {msg.content && (
          <div className="mt-1">
            <Markdown content={msg.content} />
            {live && <span className="ml-[2px] inline-block h-[14px] w-[7px] translate-y-[2px] animate-ws-caret bg-ws-fg-2" />}
          </div>
        )}
        {live?.notice && <p className="m-0 mt-1 text-[12px] tracking-normal text-ws-fg-4">{live.notice}</p>}
        {cards.length > 0 && <div className="mt-3 flex flex-col gap-3">{cards}</div>}
        {notes && (
          <div className="mt-2 flex flex-wrap items-center gap-x-4">
            {out && (
              <button type="button" aria-expanded={log} onClick={() => setLog(!log)} className={noteBtn}>
                <ChevronRight size={12} strokeWidth={2.2} className={cx('text-ws-fg-4 transition-transform duration-150', log && 'rotate-90')} />
                {took ? `Worked for ${span(took)}` : 'What it did'}
              </button>
            )}
            {cited && (
              <button type="button" aria-expanded={src} onClick={() => setSrc(!src)} className={noteBtn}>
                <ChevronRight size={12} strokeWidth={2.2} className={cx('text-ws-fg-4 transition-transform duration-150', src && 'rotate-90')} />
                {cited.length} {cited.length === 1 ? 'source' : 'sources'}
              </button>
            )}
            {made.map((r, i) => {
              const known = r.id ? routines.find((x) => x.id === r.id) : undefined
              const body = (
                <>
                  {r.verb} routine
                  <Clock size={13} strokeWidth={2} className="text-ws-fg-4" />
                  <span className="max-w-[260px] truncate text-ws-fg-2">{r.name}</span>
                </>
              )
              return known ? (
                <button key={i} type="button" onClick={() => setS({ panel: 'routines', routineId: known.id })} className={noteBtn}>
                  {body}
                </button>
              ) : (
                <span key={i} className={note}>
                  {body}
                </span>
              )
            })}
            {saved(events) && (
              <button type="button" onClick={() => setS({ panel: 'memory' })} className={noteBtn}>
                <Brain size={13} strokeWidth={2} className="text-ws-fg-4" />
                Saved to memory
              </button>
            )}
            {msg.meta?.stopped && <span className={note}>Stopped before it finished</span>}
          </div>
        )}
        {log && <Steps msg={msg} />}
        {src && cited && <Sources list={cited} />}
      </div>
    </div>
  )
}

/** One line of a conversation: yours, an agent's answer, or a line no one typed (an automation, another agent). */
export function Message({ msg, agents, owner }: { msg: ChatMessage; agents: Agent[]; owner?: Agent }) {
  if (msg.role === 'user' && msg.meta?.trigger) {
    const t = msg.meta.trigger
    const from = t.kind === 'agent' ? agents.find((a) => a.id === t.id) : undefined
    return (
      <div className="flex flex-col items-center gap-2">
        <span className={note}>
          {t.kind === 'agent' ? 'Message from' : t.kind === 'task' ? 'Routine' : t.kind === 'worker' ? 'Worker' : t.kind === 'watch' ? 'Watcher' : 'Scan'}
          {from ? <Glyph palette={from.palette} size={14} /> : <Clock size={13} strokeWidth={2} className="text-ws-fg-4" />}
          <span className="max-w-[320px] truncate text-ws-fg-2">{t.title}</span>
          <span className="text-ws-fg-4">{clock(msg.createdAt)}</span>
        </span>
        {t.kind === 'agent' && msg.content && <div className="max-w-[86%] self-end whitespace-pre-wrap rounded-[10px] border border-ws-edge bg-ws-panel px-3 py-2 leading-[21px] tracking-normal text-ws-fg">{msg.content}</div>}
      </div>
    )
  }
  if (msg.role === 'user') {
    const { text, files } = splitAttachments(msg.content)
    return (
      <div className="flex flex-col items-end gap-2">
        {files.length > 0 && (
          <div className="flex max-w-[86%] flex-wrap justify-end gap-2">
            {files.map((f, i) => (
              <span key={i} className="flex h-8 max-w-full items-center gap-2 rounded-[8px] border border-ws-edge bg-ws-panel pl-[5px] pr-[10px] text-[12px] tracking-normal text-ws-fg-2">
                <span className="flex h-[22px] min-w-[30px] shrink-0 items-center justify-center rounded-[5px] bg-ws-bg px-1 text-[9.5px] font-semibold leading-none tracking-[0.03em]" aria-hidden>
                  {KIND(f)}
                </span>
                <span className="min-w-0 truncate">{f}</span>
              </span>
            ))}
          </div>
        )}
        {text && <div className="max-w-[86%] whitespace-pre-wrap break-words rounded-[10px] border border-ws-edge bg-ws-panel px-3 py-2 leading-[21px] tracking-normal text-ws-fg">{text}</div>}
      </div>
    )
  }
  // An automated check that found nothing: one quiet line.
  if (msg.meta?.quiet)
    return (
      <div className="flex justify-center">
        <span className={note}>
          <Clock size={13} strokeWidth={2} className="text-ws-fg-4" />
          {msg.meta.trigger?.title ?? 'Checked'} — nothing to report
          <span className="text-ws-fg-4">{clock(msg.createdAt)}</span>
        </span>
      </div>
    )
  return <Answer msg={msg} agents={agents} owner={owner} />
}

/**
 * A conversation, loaded in pieces: its latest stretch arrives first; the earlier part comes a page at a
 * time as you scroll up to it, and the line you were reading stays where it was. A long conversation
 * never arrives — or sits in the browser — whole.
 */
export function Thread({ agent, group, temp }: { agent?: Agent; group?: Group; temp?: boolean }) {
  const agents = useDemo((s) => s.agents)
  const thread = useDemo((s) => s.thread)
  const live = useDemo((s) => s.live)
  const loadEarlier = useDemo((s) => s.loadEarlier)
  const box = useRef<HTMLDivElement>(null)
  const tall = useRef(0)
  const stick = useRef(true)
  const list = thread?.messages ?? []
  const first = list[0]?.id
  const last = list[list.length - 1]
  const mine = live && thread && live.chatId === thread.id ? live : null

  // New words at the bottom keep the bottom in view — unless you scrolled up to read.
  useEffect(() => {
    const el = box.current
    if (el && stick.current) el.scrollTop = el.scrollHeight
  }, [thread?.id, last?.id, mine?.content, mine?.toolEvents.length, mine?.ack])
  useEffect(() => {
    stick.current = true
  }, [thread?.id])
  // An earlier page was added above: keep the line you were reading under your eyes.
  useLayoutEffect(() => {
    const el = box.current
    if (el && tall.current) el.scrollTop += el.scrollHeight - tall.current
    tall.current = 0
  }, [first])
  const onScroll = () => {
    const el = box.current
    if (!el) return
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120
    if (thread?.more && !thread.loading && !tall.current && el.scrollTop < 240) {
      tall.current = el.scrollHeight
      void loadEarlier()
    }
  }
  const liveMsg: ChatMessage | null = mine ? { id: mine.id || 'live', chatId: mine.chatId, role: 'assistant', content: mine.content, toolEvents: mine.toolEvents, segments: mine.segments, citations: mine.citations, meta: mine.agent ? { agent: mine.agent } : undefined, createdAt: mine.startedAt } : null
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div ref={box} onScroll={onScroll} className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-[800px] flex-col gap-6 px-4 pb-6 pt-6 md:px-0">
          {thread?.more && <div className="text-center text-[12px] leading-4 tracking-normal text-ws-fg-4">{thread.loading ? 'Loading earlier messages…' : 'Earlier messages above'}</div>}
          {thread?.loading && !list.length && <div className="text-center text-[12px] leading-4 tracking-normal text-ws-fg-4">Loading…</div>}
          {list.map((m, i) => {
            const prev = list[i - 1]
            const turn = !prev || new Date(prev.createdAt).toDateString() !== new Date(m.createdAt).toDateString()
            return (
              <div key={m.id} className="contents">
                {turn && <div className="text-center text-[12px] leading-4 tracking-normal text-ws-fg-4">{dayOf(m.createdAt)}</div>}
                <Message msg={m} agents={agents} owner={agent} />
              </div>
            )
          })}
          {liveMsg && mine && <Answer msg={liveMsg} agents={agents} owner={agent} live={mine} />}
        </div>
      </div>
      <div className="mx-auto w-full max-w-[800px] shrink-0 px-3 pb-[max(12px,env(safe-area-inset-bottom))] md:px-0 md:pb-6">
        <Composer target={temp ? undefined : (agent?.id ?? group?.id)} agent={agent} group={group} dock />
      </div>
    </div>
  )
}

/** What two agents said to each other — read only, as it happened. */
export function WireView({ id }: { id: string }) {
  const agents = useDemo((s) => s.agents)
  const setS = useDemo((s) => s.set)
  const [messages, setMessages] = useState<ChatMessage[] | null>(null)
  const [pair, setPair] = useState<{ from: string; to: string } | null>(null)
  useEffect(() => {
    let on = true
    setMessages(null)
    chatPage(id, null, 200)
      .then((p) => on && (setMessages(p.messages), setPair(p.chat.wire ?? null)))
      .catch(() => on && setMessages([]))
    return () => {
      on = false
    }
  }, [id])
  const a = agents.find((x) => x.id === pair?.from)
  const b = agents.find((x) => x.id === pair?.to)
  return (
    <div className="flex min-h-0 flex-1 animate-ws-fade flex-col">
      <div className="flex shrink-0 justify-center pt-4">
        <span className="flex h-8 items-center gap-2 rounded-[8px] border border-ws-edge bg-ws-panel pl-2 pr-3 font-medium text-ws-fg">
          {a && <Glyph palette={a.palette} />}
          {a?.name ?? '…'}
          <ArrowLeftRight size={13} strokeWidth={2} className="text-ws-fg-4" />
          {b && <Glyph palette={b.palette} />}
          {b?.name ?? '…'}
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-[800px] flex-col gap-5 px-4 py-6 md:px-0">
          {messages == null && <div className="text-center text-[12px] tracking-normal text-ws-fg-4">Loading…</div>}
          {messages?.[0] && (
            <div className="text-center text-[12px] tracking-normal text-ws-fg-4">
              {dayOf(messages[0].createdAt)}, {clock(messages[0].createdAt)}
            </div>
          )}
          {messages?.map((m) =>
            m.role === 'user' ? (
              <div key={m.id} className="flex gap-3">
                <span className="mt-[2px] shrink-0">{a && <Glyph palette={a.palette} />}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex h-5 items-center gap-2">
                    <span className="font-medium text-ws-fg">{a?.name ?? m.meta?.trigger?.title}</span>
                    <span className="text-[12px] tracking-normal text-ws-fg-4">{clock(m.createdAt)}</span>
                  </div>
                  <p className="m-0 mt-1 whitespace-pre-wrap leading-[22px] tracking-normal text-[#d8d8d8]">{m.content}</p>
                </div>
              </div>
            ) : (
              <Message key={m.id} msg={m} agents={agents} owner={b} />
            ),
          )}
        </div>
      </div>
      <div className="flex shrink-0 justify-center pb-[max(16px,env(safe-area-inset-bottom))] pt-2">
        <Btn variant="outline" onClick={() => setS({ wireId: null })}>
          Close
        </Btn>
      </div>
    </div>
  )
}
